import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash, ChevronDown, ChevronUp, Hotel, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import AuthGuard from '@/components/auth/AuthGuard';
import { Database } from '@/integrations/supabase/types';
import { ImageUploader } from '@/components/ui/image-uploader';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { RichTextEditor } from '@/components/ui/rich-text-editor/RichTextEditor';

type ContentStatus = Database['public']['Enums']['content_status'];
type Json = Database['public']['Tables']['itinerary_days']['Insert']['activities'];

interface Activity {
  title: string;
  description: string;
  affiliate_link?: string;
}

interface Accommodation {
  name: string;
  booking_link?: string;
  notes?: string;
}

interface Recommendation {
  title: string;
  description?: string;
  affiliate_link?: string;
}

interface ItineraryDay {
  day_number: number;
  title: string;
  description: string;
  location?: string;
  activities: Activity[];
  accommodation?: Accommodation;
  recommendations: Recommendation[];
  open: boolean;
}

const SEASONS = ["Spring", "Summer", "Fall", "Winter", "Year-round"];

const NewItinerary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    destination: '',
    duration: '7',
    cost_estimate: '',
    featured_image: '',
    best_season: 'Year-round' as string,
    status: 'draft' as ContentStatus,
  });
  
  const [days, setDays] = useState<ItineraryDay[]>([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'title' && !formData.slug) {
      const slug = value.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleImageUploaded = (imagePath: string) => {
    setFormData(prev => ({ ...prev, featured_image: imagePath }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug) {
      toast.error('Title and slug are required');
      return;
    }
    
    setLoading(true);
    
    try {
      const { data: itineraryData, error: itineraryError } = await supabase
        .from('itineraries')
        .insert({
          title: formData.title,
          slug: formData.slug,
          description: formData.description,
          destination: formData.destination,
          duration: parseInt(formData.duration),
          cost_estimate: formData.cost_estimate ? parseFloat(formData.cost_estimate) : null,
          featured_image: formData.featured_image,
          status: formData.status,
          author_id: user?.id,
          best_season: formData.best_season
        })
        .select();
      
      if (itineraryError) {
        throw itineraryError;
      }
      
      const itineraryId = itineraryData[0].id;
      
      if (days.length > 0) {
        const daysForInsertion = days.map(day => ({
          itinerary_id: itineraryId,
          day_number: day.day_number,
          title: day.title,
          description: day.description,
          location: day.location || null,
          activities: day.activities as unknown as Json,
          accommodation: day.accommodation as unknown as Json,
          recommendations: day.recommendations as unknown as Json
        }));
        
        const { error: daysError } = await supabase
          .from('itinerary_days')
          .insert(daysForInsertion);
        
        if (daysError) {
          throw daysError;
        }
      }
      
      toast.success('Itinerary created successfully');
      navigate('/dashboard/itineraries');
    } catch (error: any) {
      toast.error(`Error creating itinerary: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const addDay = () => {
    const newDayNumber = days.length > 0 ? Math.max(...days.map(day => day.day_number)) + 1 : 1;
    const newDay: ItineraryDay = {
      day_number: newDayNumber,
      title: `Day ${newDayNumber}`,
      description: '',
      activities: [],
      recommendations: [],
      open: true
    };
    
    setDays([...days, newDay]);
  };
  
  const removeDay = (dayNumber: number) => {
    const updatedDays = days.filter(day => day.day_number !== dayNumber)
      .map((day, index) => ({
        ...day,
        day_number: index + 1
      }));
    setDays(updatedDays);
  };
  
  const updateDay = (dayNumber: number, field: string, value: any) => {
    const updatedDays = days.map(day => 
      day.day_number === dayNumber ? { ...day, [field]: value } : day
    );
    setDays(updatedDays);
  };
  
  const toggleDayOpen = (dayNumber: number) => {
    const updatedDays = days.map(day => 
      day.day_number === dayNumber ? { ...day, open: !day.open } : day
    );
    setDays(updatedDays);
  };
  
  const addActivity = (dayNumber: number) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        return {
          ...day,
          activities: [...day.activities, { title: '', description: '', affiliate_link: '' }]
        };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const updateActivity = (dayNumber: number, activityIndex: number, field: string, value: string) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        const updatedActivities = [...day.activities];
        updatedActivities[activityIndex] = { 
          ...updatedActivities[activityIndex], 
          [field]: value 
        };
        return { ...day, activities: updatedActivities };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const removeActivity = (dayNumber: number, activityIndex: number) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        const updatedActivities = day.activities.filter((_, index) => index !== activityIndex);
        return { ...day, activities: updatedActivities };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const updateAccommodation = (dayNumber: number, field: string, value: string) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        const currentAccommodation = day.accommodation || { name: '', booking_link: '', notes: '' };
        return {
          ...day,
          accommodation: { ...currentAccommodation, [field]: value }
        };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const addRecommendation = (dayNumber: number) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        return {
          ...day,
          recommendations: [...day.recommendations, { title: '', description: '', affiliate_link: '' }]
        };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const updateRecommendation = (dayNumber: number, recIndex: number, field: string, value: string) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        const updatedRecs = [...day.recommendations];
        updatedRecs[recIndex] = { ...updatedRecs[recIndex], [field]: value };
        return { ...day, recommendations: updatedRecs };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const removeRecommendation = (dayNumber: number, recIndex: number) => {
    const updatedDays = days.map(day => {
      if (day.day_number === dayNumber) {
        const updatedRecs = day.recommendations.filter((_, index) => index !== recIndex);
        return { ...day, recommendations: updatedRecs };
      }
      return day;
    });
    setDays(updatedDays);
  };
  
  const initializeDays = () => {
    const numDays = parseInt(formData.duration);
    if (numDays > 0 && days.length === 0) {
      const initialDays = Array.from({ length: numDays }, (_, i) => ({
        day_number: i + 1,
        title: `Day ${i + 1}`,
        description: '',
        activities: [],
        recommendations: [],
        open: i === 0
      }));
      setDays(initialDays);
    }
  };
  
  return (
    <AuthGuard requireEditor>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">New Itinerary</h1>
            <p className="text-muted-foreground">Create and publish a new travel itinerary</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <Card className="glass-card mb-6">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Enter the general information for your itinerary
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input 
                    id="title" 
                    name="title" 
                    placeholder="7 Days in Portugal" 
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="slug">URL ID *</Label>
                  <Input 
                    id="slug" 
                    name="slug" 
                    placeholder="7-days-in-portugal" 
                    value={formData.slug}
                    onChange={handleChange}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    This will be used in the URL. Use only lowercase letters, numbers, and hyphens.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="destination">Location *</Label>
                  <Input 
                    id="destination" 
                    name="destination" 
                    placeholder="Lisbon, Portugal" 
                    value={formData.destination}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="best_season">Best Season</Label>
                  <Select
                    value={formData.best_season}
                    onValueChange={(value) => handleSelectChange('best_season', value)}
                  >
                    <SelectTrigger id="best_season">
                      <SelectValue placeholder="Select season" />
                    </SelectTrigger>
                    <SelectContent>
                      {SEASONS.map(season => (
                        <SelectItem key={season} value={season}>{season}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => handleSelectChange('description', value)}
                  placeholder="A brief description of the itinerary"
                  minHeight={150}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (days) *</Label>
                  <Input 
                    id="duration" 
                    name="duration" 
                    type="number" 
                    min="1"
                    value={formData.duration}
                    onChange={(e) => {
                      handleChange(e);
                      if (days.length > 0) {
                        setDays([]);
                      }
                    }}
                    onBlur={initializeDays}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cost_estimate">Estimated Cost ($)</Label>
                  <Input 
                    id="cost_estimate" 
                    name="cost_estimate" 
                    type="number" 
                    min="0"
                    step="0.01"
                    placeholder="1000.00" 
                    value={formData.cost_estimate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <ImageUploader 
                onImageUploaded={handleImageUploaded}
                currentImage={formData.featured_image}
                label="Featured Image (Cover)"
              />
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => handleSelectChange('status', value as ContentStatus)}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Daily Itinerary</CardTitle>
                <CardDescription>Add details for each day of the trip</CardDescription>
              </div>
              <Button type="button" onClick={addDay}>
                <Plus className="mr-2 h-4 w-4" /> Add Day
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {days.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center border border-dashed rounded-lg">
                  <p className="mb-2 text-muted-foreground">No days added yet.</p>
                  <Button type="button" onClick={addDay} variant="outline" size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Add Day
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {days.map((day) => (
                    <Collapsible key={day.day_number} open={day.open} onOpenChange={() => toggleDayOpen(day.day_number)}>
                      <div className="border rounded-lg">
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center justify-between p-4 cursor-pointer bg-muted/50 rounded-t-lg">
                            <div className="flex items-center">
                              <div className="bg-primary/10 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">
                                {day.day_number}
                              </div>
                              <div className="font-medium">
                                {day.title || `Day ${day.day_number}`}
                              </div>
                            </div>
                            <div className="flex items-center">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="icon" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDay(day.day_number);
                                }}
                              >
                                <Trash className="h-4 w-4 text-destructive" />
                              </Button>
                              {day.open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor={`day-${day.day_number}-title`}>Day Title</Label>
                                <Input
                                  id={`day-${day.day_number}-title`}
                                  value={day.title}
                                  onChange={(e) => updateDay(day.day_number, 'title', e.target.value)}
                                  placeholder="Arrival in Lisbon"
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label htmlFor={`day-${day.day_number}-location`}>Location</Label>
                                <div className="flex items-center space-x-2">
                                  <MapPin className="h-4 w-4 text-muted-foreground" />
                                  <Input
                                    id={`day-${day.day_number}-location`}
                                    value={day.location || ''}
                                    onChange={(e) => updateDay(day.day_number, 'location', e.target.value)}
                                    placeholder="Lisbon"
                                  />
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor={`day-${day.day_number}-description`}>Description</Label>
                              <RichTextEditor
                                value={day.description}
                                onChange={(value) => updateDay(day.day_number, 'description', value)}
                                placeholder="Describe the day's highlights"
                                minHeight={150}
                              />
                            </div>
                            
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label>Activities</Label>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => addActivity(day.day_number)}
                                >
                                  <Plus className="mr-1 h-3 w-3" /> Add Activity
                                </Button>
                              </div>
                              
                              {day.activities.length === 0 ? (
                                <div className="text-sm text-muted-foreground italic px-2">
                                  No activities added yet
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {day.activities.map((activity, activityIndex) => (
                                    <div key={activityIndex} className="border rounded-md p-3 space-y-2">
                                      <div className="flex justify-between items-start">
                                        <Label htmlFor={`activity-${day.day_number}-${activityIndex}-title`}>
                                          Activity Title
                                        </Label>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => removeActivity(day.day_number, activityIndex)}
                                        >
                                          <Trash className="h-3 w-3 text-destructive" />
                                        </Button>
                                      </div>
                                      
                                      <Input
                                        id={`activity-${day.day_number}-${activityIndex}-title`}
                                        value={activity.title}
                                        onChange={(e) => updateActivity(day.day_number, activityIndex, 'title', e.target.value)}
                                        placeholder="Visit Santa Justa Lift"
                                      />
                                      
                                      <RichTextEditor
                                        value={activity.description}
                                        onChange={(value) => updateActivity(day.day_number, activityIndex, 'description', value)}
                                        placeholder="Activity description"
                                        minHeight={100}
                                      />
                                      
                                      <div className="space-y-1">
                                        <Label htmlFor={`activity-${day.day_number}-${activityIndex}-link`}>
                                          Affiliate Link (optional)
                                        </Label>
                                        <Input
                                          id={`activity-${day.day_number}-${activityIndex}-link`}
                                          value={activity.affiliate_link || ''}
                                          onChange={(e) => updateActivity(day.day_number, activityIndex, 'affiliate_link', e.target.value)}
                                          placeholder="https://booking-site.com/affiliate-link"
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <div className="space-y-3 border-t pt-4">
                              <div className="flex items-center">
                                <Hotel className="h-4 w-4 mr-2 text-muted-foreground" />
                                <Label>Accommodation</Label>
                              </div>
                              
                              <div className="space-y-2">
                                <Input
                                  placeholder="Hotel or Accommodation Name"
                                  value={day.accommodation?.name || ''}
                                  onChange={(e) => updateAccommodation(day.day_number, 'name', e.target.value)}
                                />
                                
                                <div className="space-y-1">
                                  <Label>Booking Link (affiliate)</Label>
                                  <Input
                                    placeholder="https://booking.com/your-affiliate-link"
                                    value={day.accommodation?.booking_link || ''}
                                    onChange={(e) => updateAccommodation(day.day_number, 'booking_link', e.target.value)}
                                  />
                                </div>
                                
                                <RichTextEditor
                                  placeholder="Notes about the accommodation"
                                  value={day.accommodation?.notes || ''}
                                  onChange={(value) => updateAccommodation(day.day_number, 'notes', value)}
                                  minHeight={100}
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-3 border-t pt-4">
                              <div className="flex items-center justify-between">
                                <Label>Recommended Experiences</Label>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => addRecommendation(day.day_number)}
                                >
                                  <Plus className="mr-1 h-3 w-3" /> Add Recommendation
                                </Button>
                              </div>
                              
                              {day.recommendations.length === 0 ? (
                                <div className="text-sm text-muted-foreground italic px-2">
                                  No recommendations added yet
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {day.recommendations.map((rec, recIndex) => (
                                    <div key={recIndex} className="border rounded-md p-3 space-y-2">
                                      <div className="flex justify-between items-start">
                                        <Label htmlFor={`rec-${day.day_number}-${recIndex}-title`}>
                                          Recommendation
                                        </Label>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => removeRecommendation(day.day_number, recIndex)}
                                        >
                                          <Trash className="h-3 w-3 text-destructive" />
                                        </Button>
                                      </div>
                                      
                                      <Input
                                        id={`rec-${day.day_number}-${recIndex}-title`}
                                        value={rec.title}
                                        onChange={(e) => updateRecommendation(day.day_number, recIndex, 'title', e.target.value)}
                                        placeholder="Try local pastries"
                                      />
                                      
                                      <RichTextEditor
                                        value={rec.description || ''}
                                        onChange={(value) => updateRecommendation(day.day_number, recIndex, 'description', value)}
                                        placeholder="Why we recommend this"
                                        minHeight={100}
                                      />
                                      
                                      <div className="space-y-1">
                                        <Label htmlFor={`rec-${day.day_number}-${recIndex}-link`}>
                                          Affiliate Link (optional)
                                        </Label>
                                        <Input
                                          id={`rec-${day.day_number}-${recIndex}-link`}
                                          value={rec.affiliate_link || ''}
                                          onChange={(e) => updateRecommendation(day.day_number, recIndex, 'affiliate_link', e.target.value)}
                                          placeholder="https://booking-site.com/affiliate-link"
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard/itineraries')}
                type="button"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Itinerary'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </AuthGuard>
  );
};

export default NewItinerary;

